import { useTheme } from '@mui/material';
import {
  ChartOptions,
  ColorType,
  CrosshairMode,
  DeepPartial,
  IChartApi,
  createChart,
} from 'lightweight-charts';
import { MutableRefObject } from 'react';

type initChartType = {
  containerRef: MutableRefObject<any>;
  chartRef: MutableRefObject<IChartApi | undefined>;
  actions: {
    subscribeClick?: (param: any) => void;
    subscribeCrosshairMove?: (param: any) => void;
  };
  options?: DeepPartial<ChartOptions>;
};

export const useChartSetup = () => {
  const theme = useTheme();

  const initMainChart = ({
    containerRef,
    chartRef,
    actions: { subscribeClick, subscribeCrosshairMove },
    options,
  }: initChartType) => {
    if (!containerRef.current) return;
    const mainChart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
      autoSize: options?.autoSize ?? true,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: theme.palette.text.primary,
      },
      grid: {
        vertLines: {
          color: '#002649',
        },
        horzLines: {
          color: '#002649',
        },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      timeScale: {
        borderColor: theme.palette.divider,
        timeVisible: true,
        visible: true,
        barSpacing: 20,
      },
      rightPriceScale: {
        autoScale: true,
      },
      leftPriceScale: {
        autoScale: true,
      },
    });

    if (subscribeClick) {
      mainChart?.subscribeClick(subscribeClick);
    }

    if (subscribeCrosshairMove) {
      mainChart?.subscribeCrosshairMove(subscribeCrosshairMove);
    }

    chartRef.current = mainChart;
    return chartRef.current;
  };

  const initFlyChart = ({
    containerRef,
    chartRef,
    actions: { subscribeClick },
    options,
  }: initChartType) => {
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = undefined;
    }

    const flyChart = createChart(containerRef.current, {
      height: containerRef.current?.clientHeight,
      autoSize: options?.autoSize ?? true,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: theme.palette.text.primary,
      },
      grid: {
        vertLines: {
          color: '#002649',
        },
        horzLines: {
          color: '#002649',
        },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      timeScale: {
        borderColor: theme.palette.divider,
        timeVisible: true,
        visible: true,
        barSpacing: 20,
      },
      rightPriceScale: {
        autoScale: true,
      },
      leftPriceScale: {
        autoScale: true,
      },
    });

    if (subscribeClick) {
      flyChart?.subscribeClick(subscribeClick);
    }

    chartRef.current = flyChart;
    return chartRef.current;
  };

  return {
    initMainChart,
    initFlyChart,
  };
};
